import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface PhotonTimelineProps {
  width?: number;
  height?: number;
}

export const PhotonTimeline: React.FC<PhotonTimelineProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerY = size / 2;
    const padding = 40;
    const trackWidth = size - padding * 2;
    const startX = padding;
    const endX = size - padding;

    // Timeline Line
    ctx.beginPath();
    ctx.moveTo(startX, centerY);
    ctx.lineTo(endX, centerY);
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.stroke();

    // 8 seconds at 60fps = 480 frames. Add 2 seconds (120 frames) for text display before loop.
    const loopDuration = 600;
    const currentFrame = frameCount % loopDuration;
    const isTraveling = currentFrame < 480;
    
    const progress = Math.min(1, currentFrame / 480);
    const photonX = startX + progress * trackWidth;

    // Draw Star (Left)
    ctx.save();
    const pulse = Math.sin(frameCount * 0.1) * 2;
    const starGrad = ctx.createRadialGradient(startX, centerY, 2, startX, centerY, 20 + pulse);
    starGrad.addColorStop(0, '#FFFFFF');
    starGrad.addColorStop(0.3, '#FDE047');
    starGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = starGrad;
    ctx.beginPath();
    ctx.arc(startX, centerY, 20 + pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw Telescope/Eye (Right)
    ctx.fillStyle = '#94A3B8';
    ctx.beginPath();
    ctx.arc(endX, centerY, 15, Math.PI * 1.2, Math.PI * 2.8);
    ctx.lineTo(endX - 15, centerY);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = '#0F172A';
    ctx.arc(endX + 3, centerY, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#60A5FA';
    ctx.beginPath();
    ctx.arc(endX + 3, centerY, 3, 0, Math.PI * 2);
    ctx.fill();

    // Draw Photon
    if (isTraveling || currentFrame >= 480) {
      ctx.fillStyle = '#FDE047';
      ctx.beginPath();
      ctx.arc(photonX, centerY, 6, 0, Math.PI * 2);
      ctx.fill();
      
      // Photon glow
      ctx.fillStyle = 'rgba(253, 224, 71, 0.4)';
      ctx.beginPath();
      ctx.arc(photonX, centerY, 12, 0, Math.PI * 2);
      ctx.fill();
      
      // Label below photon
      if (isTraveling) {
        ctx.fillStyle = '#E2E8F0';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Light takes 10 years', Math.max(startX + 60, Math.min(photonX, endX - 60)), centerY + 25);
      }
    }

    // Static Labels
    ctx.textAlign = 'center';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillStyle = '#F8FAFC';
    ctx.fillText('2016', startX, centerY - 35);
    ctx.fillText('2026', endX, centerY - 35);

    ctx.font = '12px sans-serif';
    ctx.fillStyle = '#94A3B8';
    ctx.fillText('Star looks like this now', startX, centerY + 35);
    ctx.fillText('We see this today', endX, centerY + 35);

    // End text
    if (!isTraveling) {
      ctx.fillStyle = '#4ADE80';
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('We see 2016 light in 2026!', size / 2, centerY - 15);
    }

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
